import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import {
  githubAuthApiRef,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';

/**
 * Custom SignInPage extension that wires up GitHub OAuth as the sole sign-in
 * provider.
 *
 * Why this exists:
 * - The default SignInPage shipped by @backstage/plugin-app hardcodes
 *   `providers: ["guest"]` in its loader. Since the guest provider module was
 *   removed from the backend entirely (2026-08-16), that default page would
 *   post to a route that no longer exists — the user would get a dead
 *   "Failed to sign in as a guest" dialog and no path to authenticate at all.
 * - There is no app-config key that lets you change the default page's
 *   providers; the only way to swap providers is to override the extension.
 * - Because SignInPageBlueprint attaches to `app/root`'s `signInPage`
 *   singleton input, registering this module in `App.tsx#features`
 *   automatically *replaces* the default extension.
 *
 * Trio that has to be wired up correctly for sign-in to work end-to-end:
 *   1. Backend module:  `backend.add(import('@backstage/plugin-auth-backend-module-github-provider'))`
 *      → see packages/backend/src/index.ts
 *   2. Backend config:  `auth.providers.github` block in app-config.production.yaml
 *      (+ SSM-sourced AUTH_GITHUB_CLIENT_ID / AUTH_GITHUB_CLIENT_SECRET)
 *   3. Frontend SignInPage extension  ← this file
 */
// Sign-in providers: GitHub (org members) + Microsoft/Entra (non-GitHub staff).
// The 'guest' bypass was REMOVED 2026-08-16 — it granted anyone an admin
// session. Automated smoke now authenticates via a static service token, not
// guest (see scripts/smoke + app-config.production.yaml backend.auth.externalAccess).
const githubSignInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => {
      const { SignInPage } = await import('@backstage/core-components');
      return props => (
        <SignInPage
          {...props}
          providers={[
            {
              id: 'github-auth-provider',
              title: 'GitHub',
              message: 'Sign in using AGM-One-Vision GitHub OAuth',
              apiRef: githubAuthApiRef,
            },
            // Microsoft/Entra — for AGM staff not in the GitHub org (e.g.
            // read-only business users). Backed by auth.providers.microsoft
            // in app-config.production.yaml. See docs/operations.md.
            {
              id: 'microsoft-auth-provider',
              title: 'Microsoft',
              message: 'Sign in with your @aboughalymotors.com account',
              apiRef: microsoftAuthApiRef,
            },
          ]}
        />
      );
    },
  },
});

export const signInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [githubSignInPage],
});
